import type { ReactNode } from "react";
import { ChevronLeft } from "lucide-react";
import Stack from "@mui/material/Stack";
import { Divider, useTheme } from "@mui/material";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";

/**
 * The card a takeover view sits in: the back row that names the host, then the
 * view.
 *
 * The rule workspace brings its own frame (`WorkspacePageFrame`, which carries
 * the same back row plus the rule's title bar and tabs), so this is for the
 * views that have no workspace of their own — the creation gallery and the
 * designing sketch.
 */
export const TakeoverFrame = ({
  backLabel,
  onBack,
  children,
}: {
  backLabel: string;
  onBack: () => void;
  children: ReactNode;
}) => {
  const theme = useTheme();
  return (
    <IgPaper
      sx={{
        borderColor: theme.igBorders.core.default,
        p: 0,
        borderRadius: 1,
        overflow: "hidden",
        // Fills the host's content area, as the rule workspace beside it does.
        // The card is the page here; a card sized to its own content would
        // float in an empty dialog.
        display: "flex",
        flex: 1,
        minHeight: 0,
      }}
    >
      <Stack sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <Stack sx={{ px: 3, py: 2 }} alignItems="flex-start">
          <IgButton
            variant="text"
            color="secondary"
            onClick={onBack}
            startIcon={<ChevronLeft size={16} />}
          >
            {backLabel}
          </IgButton>
        </Stack>
        <Divider />
        <Stack sx={{ px: 6, py: 5, flex: 1, minHeight: 0, overflowY: "auto" }}>
          {children}
        </Stack>
      </Stack>
    </IgPaper>
  );
};
